import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecurring } from './contexts/RecurringContext';
import { useTimeTable } from './contexts/TimeTableContext';
import RecurringPreview from './components/RecurringPreview';
import ClockPicker from './components/ClockPicker';
import ConfirmationDialog from './ConfirmationDialog';
import './css/RecurringSchedule.css';

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const INITIAL_FORM = {
    schedule_type: 'timed',
    subject_id: '',
    special_text: '',
    description: '',
    location: '',
    recurrence_type: 'weekly',
    recurrence_interval: 1,
    recurrence_days: [],
    recurrence_month_day: 1,
    recurrence_month_week: 1,
    recurrence_month_weekday: 0,
    start_date: '',
    end_condition: 'date',
    end_date: '',
    max_occurrences: 10,
    start_time_of_day: '09:00',
    scheduled_time: 60,
};

/* ── icons (inline SVG) ── */
const Icon = ({ d, size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);
const IconSubject = () => <Icon d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />;
const IconRepeat  = () => <Icon d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3" />;
const IconClock   = () => <Icon d="M12 2a10 10 0 100 20 10 10 0 000-20zM12 6v6l4 2" />;
const IconCalendar = () => <Icon d="M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zM16 2v4M8 2v4M3 10h18" />;

function getRecurrenceDescription(rule, t) {
    const { recurrence_type, recurrence_interval: n, recurrence_days, recurrence_month_day, recurrence_month_week, recurrence_month_weekday } = rule;
    switch (recurrence_type) {
        case 'daily':
            return n === 1 ? t('recurring.list.everyDay') : t('recurring.list.everyNDays', { n });
        case 'weekly': {
            const base = n === 1 ? t('recurring.list.everyWeek') : t('recurring.list.everyNWeeks', { n });
            if (recurrence_days?.length > 0) {
                const dayNames = recurrence_days.slice().sort((a, b) => a - b).map(d => t(DAY_KEYS[d]).charAt(0)).join(',');
                return `${base} ${dayNames}`;
            }
            return base;
        }
        case 'monthly_date': {
            const base = n === 1 ? t('recurring.list.everyMonth') : t('recurring.list.everyNMonths', { n });
            return `${base} ${recurrence_month_day}${t('recurring.recurrence.monthDay')}`;
        }
        case 'monthly_weekday': {
            const base = n === 1 ? t('recurring.list.everyMonth') : t('recurring.list.everyNMonths', { n });
            return `${base} ${t('recurring.list.nthWeekday', { week: recurrence_month_week, day: t(DAY_KEYS[recurrence_month_weekday]) })}`;
        }
        default: return '';
    }
}

const RecurringSchedule = () => {
    const { t } = useTranslation();
    const { rules, loading, fetchRules, createRule, updateRule, deleteRule, previewRule } = useRecurring();
    const { subjects, fetchSubjects } = useTimeTable();

    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [editingRuleId, setEditingRuleId] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [previewData, setPreviewData] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showSubjectPicker, setShowSubjectPicker] = useState(false);
    const [subjectSearch, setSubjectSearch] = useState('');
    const [showMore, setShowMore] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [showClockPicker, setShowClockPicker] = useState(false);

    useEffect(() => {
        fetchRules();
        fetchSubjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchRules]);

    const resetForm = useCallback(() => {
        setForm({ ...INITIAL_FORM });
        setEditingRuleId(null);
        setShowMore(false);
        setValidationErrors([]);
    }, []);

    const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const toggleDay = (day) => {
        setForm(prev => {
            const days = prev.recurrence_days.includes(day)
                ? prev.recurrence_days.filter(d => d !== day)
                : [...prev.recurrence_days, day];
            return { ...prev, recurrence_days: days };
        });
    };

    const loadRuleToForm = (rule) => {
        setEditingRuleId(rule.rule_id);
        setForm({
            schedule_type: rule.schedule_type || 'timed',
            subject_id: rule.subject_id || '',
            special_text: rule.title || '',
            description: rule.description || '',
            location: rule.location || '',
            recurrence_type: rule.recurrence_type || 'weekly',
            recurrence_interval: rule.recurrence_interval || 1,
            recurrence_days: rule.recurrence_days || [],
            recurrence_month_day: rule.recurrence_month_day || 1,
            recurrence_month_week: rule.recurrence_month_week || 1,
            recurrence_month_weekday: rule.recurrence_month_weekday || 0,
            start_date: rule.start_date ? rule.start_date.substring(0, 10) : '',
            end_condition: rule.max_occurrences ? 'count' : 'date',
            end_date: rule.end_date ? rule.end_date.substring(0, 10) : '',
            max_occurrences: rule.max_occurrences || 10,
            start_time_of_day: rule.start_time_of_day ? rule.start_time_of_day.substring(0, 5) : '09:00',
            scheduled_time: rule.scheduled_time || 60,
        });
        if (rule.title || rule.description || rule.location) setShowMore(true);
        setValidationErrors([]);
    };

    const buildPayload = () => {
        const payload = {
            subject_id: Number(form.subject_id),
            schedule_type: form.schedule_type,
            title: form.special_text.trim() || null,
            description: form.description || null,
            location: form.location || null,
            recurrence_type: form.recurrence_type,
            recurrence_interval: Number(form.recurrence_interval),
            start_date: form.start_date,
        };
        if (form.schedule_type === 'timed') {
            payload.start_time_of_day = form.start_time_of_day + ':00';
            payload.scheduled_time = Number(form.scheduled_time);
        }
        if (form.recurrence_type === 'weekly') payload.recurrence_days = form.recurrence_days;
        if (form.recurrence_type === 'monthly_date') payload.recurrence_month_day = Number(form.recurrence_month_day);
        if (form.recurrence_type === 'monthly_weekday') {
            payload.recurrence_month_week = Number(form.recurrence_month_week);
            payload.recurrence_month_weekday = Number(form.recurrence_month_weekday);
        }
        if (form.end_condition === 'date') {
            payload.end_date = form.end_date;
            payload.max_occurrences = null;
        } else {
            payload.max_occurrences = Number(form.max_occurrences);
            const maxEnd = new Date(form.start_date);
            maxEnd.setFullYear(maxEnd.getFullYear() + 1);
            const pad = (n) => String(n).padStart(2, '0');
            payload.end_date = `${maxEnd.getFullYear()}-${pad(maxEnd.getMonth() + 1)}-${pad(maxEnd.getDate())}`;
        }
        return payload;
    };

    const validateForm = () => {
        const errors = [];
        if (!form.subject_id) errors.push(t('recurring.form.subject'));
        if (!form.start_date) errors.push(t('recurring.form.startDate'));
        if (form.end_condition === 'date' && !form.end_date) errors.push(t('recurring.form.endDate'));
        if (form.recurrence_type === 'weekly' && form.recurrence_days.length === 0) errors.push(t('recurring.recurrence.days'));
        setValidationErrors(errors);
        return errors.length === 0;
    };

    const handlePreview = async () => {
        if (!validateForm()) return;
        const result = await previewRule(buildPayload());
        if (result) { setPreviewData(result); setShowPreview(true); }
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        const payload = buildPayload();
        const result = editingRuleId ? await updateRule(editingRuleId, payload) : await createRule(payload);
        if (result) resetForm();
    };

    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

    const handleDelete = () => {
        if (!editingRuleId) return;
        setConfirmDialog({
            isOpen: true,
            title: t('confirm'),
            message: t('recurring.list.deleteConfirm'),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                if (await deleteRule(editingRuleId)) resetForm();
            }
        });
    };

    const selectedSubject = subjects.find(s => s.subject_id === Number(form.subject_id));
    const filteredSubjects = subjectSearch
        ? subjects.filter(s => s.subjectname.toLowerCase().includes(subjectSearch.toLowerCase()) || (s.category?.category_name || '').toLowerCase().includes(subjectSearch.toLowerCase()))
        : subjects;

    const selectSubject = (s) => { updateField('subject_id', s.subject_id); setShowSubjectPicker(false); setSubjectSearch(''); };

    const getSubjectForRule = (rule) => rule.StudySubject || subjects.find(s => s.subject_id === rule.subject_id);
    const getCompletionRate = (rule) => {
        const stats = rule.stats || {};
        if (!stats.total_instances) return 0;
        return Math.round((stats.completed_instances || 0) / stats.total_instances * 100);
    };

    /* ── Natural language summary ── */
    const summary = useMemo(() => {
        const parts = [];
        // recurrence
        if (form.recurrence_type === 'daily') {
            parts.push(t('recurring.recurrence.daily'));
        } else if (form.recurrence_type === 'weekly') {
            const intervalLabel = form.recurrence_interval === 1 ? t('recurring.interval.everyWeek')
                : form.recurrence_interval === 2 ? t('recurring.interval.every2Weeks')
                : form.recurrence_interval === 3 ? t('recurring.interval.every3Weeks')
                : t('recurring.interval.every4Weeks');
            const dayNames = form.recurrence_days.slice().sort((a, b) => a - b).map(d => t(DAY_KEYS[d])).join(', ');
            parts.push(`${intervalLabel}${dayNames ? ' ' + dayNames : ''}`);
        } else if (form.recurrence_type === 'monthly_date') {
            const intervalLabel = form.recurrence_interval === 1 ? t('recurring.interval.everyMonth') : t('recurring.interval.every2Months');
            parts.push(`${intervalLabel} ${form.recurrence_month_day}${t('recurring.recurrence.monthDay')}`);
        } else if (form.recurrence_type === 'monthly_weekday') {
            const intervalLabel = form.recurrence_interval === 1 ? t('recurring.interval.everyMonth') : t('recurring.interval.every2Months');
            parts.push(`${intervalLabel} ${form.recurrence_month_week}${t('recurring.recurrence.monthWeek')} ${t(DAY_KEYS[form.recurrence_month_weekday])}`);
        }
        // time
        if (form.schedule_type === 'timed' && form.start_time_of_day) {
            parts.push(`${form.start_time_of_day} ~ ${form.scheduled_time}${t('recurring.form.duration').replace(/[^(]*\(/, '').replace(')', '')}`);
        }
        // period
        if (form.start_date) {
            const end = form.end_condition === 'date' && form.end_date ? form.end_date : `${form.max_occurrences}${t('recurring.recurrence.occurrences')}`;
            parts.push(`${form.start_date} ~ ${end}`);
        }
        return parts.join('  |  ');
    }, [form, t]);

    const topBarColor = selectedSubject?.color || '#e0e0e0';

    return (
        <div className="recurring-container">
            <h2>{editingRuleId ? t('recurring.editRule') : t('recurring.addNew')}</h2>

            <div className="recurring-edit-area">
                <div className="recurring-edit-top-bar" style={{ background: topBarColor }} />
                <div className="recurring-edit-body">

                    {/* Row 1: Title + Type pills */}
                    <div className="rc-row" style={{ borderTop: 'none' }}>
                        <div className="rc-icon"><IconSubject /></div>
                        <div className="rc-content">
                            <div className="rc-inline-group" style={{ marginBottom: 6 }}>
                                <button className={`rc-subject-chip${selectedSubject ? ' has-subject' : ''}`} type="button" onClick={() => setShowSubjectPicker(true)}>
                                    <span className="rc-subject-dot" style={{ background: selectedSubject?.color || '#ccc' }} />
                                    {selectedSubject
                                        ? <>{selectedSubject.subjectname}</>
                                        : <span style={{ color: 'var(--text-secondary)' }}>{t('recurring.form.subject')}</span>
                                    }
                                    <span className="rc-chip-action">{selectedSubject ? '✎' : t('recurring.form.selectAction')}</span>
                                </button>
                                <span className="rc-pill-label" style={{ marginLeft: 8 }}>{t('recurring.type.schedule')}/{t('recurring.type.todo')}</span>
                                <button className={`rc-pill ${form.schedule_type === 'timed' ? 'active' : ''}`} onClick={() => updateField('schedule_type', 'timed')}>{t('recurring.type.schedule')}</button>
                                <button className={`rc-pill ${form.schedule_type === 'todo' ? 'active-orange' : ''}`} onClick={() => updateField('schedule_type', 'todo')}>{t('recurring.type.todo')}</button>
                            </div>
                            {/* More options */}
                            <button className="rc-more-toggle" onClick={() => setShowMore(!showMore)} type="button">
                                {showMore ? '- ' : '+ '}{t('recurring.form.ruleTitle')}, {t('recurring.form.description')}, {t('recurring.form.location')}
                            </button>
                            {showMore && (
                                <div className="rc-more-fields">
                                    <input type="text" placeholder={t('recurring.form.ruleTitle')} value={form.special_text} onChange={e => updateField('special_text', e.target.value)} />
                                    <textarea placeholder={t('recurring.form.description')} value={form.description} onChange={e => updateField('description', e.target.value)} rows={2} />
                                    <input type="text" placeholder={t('recurring.form.location')} value={form.location} onChange={e => updateField('location', e.target.value)} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Row 2+3: Recurrence + Date side-by-side */}
                    <div className="rc-row-pair">
                        <div className="rc-half">
                            <div className="rc-icon"><IconRepeat /></div>
                            <div className="rc-content">
                                <div className="rc-pills">
                                    <button className={`rc-pill ${form.recurrence_type === 'daily' ? 'active' : ''}`} onClick={() => { updateField('recurrence_type', 'daily'); updateField('recurrence_interval', 1); }}>{t('recurring.recurrence.daily')}</button>
                                    <button className={`rc-pill ${form.recurrence_type === 'weekly' ? 'active' : ''}`} onClick={() => { updateField('recurrence_type', 'weekly'); updateField('recurrence_interval', 1); }}>{t('recurring.recurrence.weekly')}</button>
                                    <button className={`rc-pill ${form.recurrence_type === 'monthly_date' || form.recurrence_type === 'monthly_weekday' ? 'active' : ''}`} onClick={() => { updateField('recurrence_type', 'monthly_date'); updateField('recurrence_interval', 1); }}>{t('recurring.recurrence.monthly')}</button>
                                </div>

                                {form.recurrence_type === 'weekly' && (
                                    <>
                                        <div className="rc-inline-group" style={{ marginTop: 8 }}>
                                            <select className="rc-select" value={form.recurrence_interval} onChange={e => updateField('recurrence_interval', Number(e.target.value))}>
                                                <option value={1}>{t('recurring.interval.everyWeek')}</option>
                                                <option value={2}>{t('recurring.interval.every2Weeks')}</option>
                                                <option value={3}>{t('recurring.interval.every3Weeks')}</option>
                                                <option value={4}>{t('recurring.interval.every4Weeks')}</option>
                                            </select>
                                        </div>
                                        <div className="rc-days">
                                            {DAY_KEYS.map((dayKey, idx) => (
                                                <button key={idx} type="button" className={`rc-day ${form.recurrence_days.includes(idx) ? 'active' : ''}`} onClick={() => toggleDay(idx)}>{t(dayKey).charAt(0)}</button>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {(form.recurrence_type === 'monthly_date' || form.recurrence_type === 'monthly_weekday') && (
                                    <div className="rc-inline-group" style={{ marginTop: 8 }}>
                                        <select className="rc-select" value={form.recurrence_interval} onChange={e => updateField('recurrence_interval', Number(e.target.value))}>
                                            <option value={1}>{t('recurring.interval.everyMonth')}</option>
                                            <option value={2}>{t('recurring.interval.every2Months')}</option>
                                            <option value={3}>{t('recurring.interval.every3Months')}</option>
                                            <option value={6}>{t('recurring.interval.every6Months')}</option>
                                        </select>
                                        <select className="rc-select" value={form.recurrence_type} onChange={e => updateField('recurrence_type', e.target.value)}>
                                            <option value="monthly_date">{t('recurring.recurrence.monthlyDate')}</option>
                                            <option value="monthly_weekday">{t('recurring.recurrence.monthlyWeekday')}</option>
                                        </select>
                                        {form.recurrence_type === 'monthly_date' && (
                                            <select className="rc-select" value={form.recurrence_month_day} onChange={e => updateField('recurrence_month_day', Number(e.target.value))}>
                                                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}{t('recurring.recurrence.monthDay')}</option>)}
                                            </select>
                                        )}
                                        {form.recurrence_type === 'monthly_weekday' && (
                                            <>
                                                <select className="rc-select" value={form.recurrence_month_week} onChange={e => updateField('recurrence_month_week', Number(e.target.value))}>
                                                    {[1, 2, 3, 4, 5].map(w => <option key={w} value={w}>{w}{t('recurring.recurrence.monthWeek')}</option>)}
                                                </select>
                                                <select className="rc-select" value={form.recurrence_month_weekday} onChange={e => updateField('recurrence_month_weekday', Number(e.target.value))}>
                                                    {DAY_KEYS.map((dk, idx) => <option key={idx} value={idx}>{t(dk)}</option>)}
                                                </select>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="rc-half">
                            <div className="rc-icon"><IconCalendar /></div>
                            <div className="rc-content">
                                <div className="rc-inline-group">
                                    <input type="date" className="rc-date-input" value={form.start_date} onChange={e => updateField('start_date', e.target.value)} onKeyDown={e => e.preventDefault()} />
                                    <span className="rc-inline-label">~</span>
                                    <label className="rc-end-radio">
                                        <input type="radio" name="end_cond" checked={form.end_condition === 'date'} onChange={() => updateField('end_condition', 'date')} />
                                        {t('recurring.recurrence.endByDate')}
                                    </label>
                                    {form.end_condition === 'date' && (
                                        <>
                                            <input type="date" className="rc-date-input" value={form.end_date} onChange={e => updateField('end_date', e.target.value)} onKeyDown={e => e.preventDefault()} />
                                        </>
                                    )}
                                    <label className="rc-end-radio">
                                        <input type="radio" name="end_cond" checked={form.end_condition === 'count'} onChange={() => updateField('end_condition', 'count')} />
                                        {t('recurring.recurrence.endByCount')}
                                    </label>
                                    {form.end_condition === 'count' && (
                                        <>
                                            <input className="rc-input rc-input-sm" type="number" min={1} value={form.max_occurrences} onChange={e => updateField('max_occurrences', e.target.value)} />
                                            <span className="rc-inline-label">{t('recurring.recurrence.occurrences')}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Row 4: Time (timed only) */}
                    {form.schedule_type === 'timed' && (
                        <div className="rc-row">
                            <div className="rc-icon"><IconClock /></div>
                            <div className="rc-content">
                                <div className="rc-inline-group">
                                    <button type="button" className="rc-time-btn" onClick={() => setShowClockPicker(true)}>
                                        <IconClock />
                                        <span className="rc-time-btn-value">{(() => {
                                            const [hh, mm] = form.start_time_of_day.split(':').map(Number);
                                            const period = hh >= 12 ? t('recurring.clock.pm') : t('recurring.clock.am');
                                            const h12 = hh % 12 || 12;
                                            return `${period} ${h12}:${String(mm).padStart(2, '0')}`;
                                        })()}</span>
                                    </button>
                                    <span className="rc-inline-label">{t('recurring.form.duration')}</span>
                                    <div className="rc-duration-group">
                                        <button type="button" className="rc-duration-btn" onClick={() => updateField('scheduled_time', Math.max(0, Number(form.scheduled_time) - 10))}>-</button>
                                        <input className="rc-input rc-duration-input" type="text" inputMode="numeric" value={form.scheduled_time}
                                            onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ''); updateField('scheduled_time', v); }}
                                        />
                                        <button type="button" className="rc-duration-btn" onClick={() => updateField('scheduled_time', Number(form.scheduled_time) + 10)}>+</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Summary */}
                {summary && <div className="rc-summary"><strong>{form.special_text || selectedSubject?.subjectname || '...'}</strong> — {summary}</div>}

                {/* Validation */}
                {validationErrors.length > 0 && (
                    <div className="rc-error">{validationErrors.join(', ')} — {t('recurring.form.required')}</div>
                )}

                {/* Actions */}
                <div className="rc-actions">
                    {editingRuleId && (
                        <>
                            <button className="rc-btn rc-btn-danger" onClick={handleDelete}>{t('recurring.delete')}</button>
                            <button className="rc-btn rc-btn-ghost" onClick={resetForm}>{t('recurring.cancel')}</button>
                        </>
                    )}
                    <button className="rc-btn rc-btn-preview" onClick={handlePreview}>{t('recurring.preview.title')}</button>
                    <button className="rc-btn rc-btn-primary" onClick={handleSave}>{t('recurring.save')}</button>
                </div>
            </div>

            {/* View toggle */}
            <div className="recurring-view-toggle">
                <button className={`recurring-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>{t('recurring.list.listView')}</button>
                <button className={`recurring-view-btn ${viewMode === 'card' ? 'active' : ''}`} onClick={() => setViewMode('card')}>{t('recurring.list.cardView')}</button>
            </div>

            {/* Rules list */}
            {loading ? (
                <p>{t('admin.users.loading')}</p>
            ) : rules.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{t('recurring.list.noRules')}</p>
            ) : viewMode === 'list' ? (
                <div className="recurring-list">
                    {rules.map(rule => {
                        const subject = getSubjectForRule(rule);
                        const rate = getCompletionRate(rule);
                        return (
                            <div key={rule.rule_id} className={`recurring-list-item ${editingRuleId === rule.rule_id ? 'editing' : ''}`} onClick={() => loadRuleToForm(rule)}>
                                <div className="recurring-list-color" style={{ backgroundColor: subject?.color || '#ccc' }} />
                                <div className="recurring-list-info">
                                    <div className="recurring-list-title">{rule.title || subject?.subjectname || ''}</div>
                                    <div className="recurring-list-meta">
                                        {subject?.subjectname || ''}
                                        <span className="recurring-list-badge">{getRecurrenceDescription(rule, t)}</span>
                                    </div>
                                </div>
                                <div className="recurring-list-stats">
                                    <span className="recurring-list-rate">{rate}%</span>
                                    <br />
                                    <span className="recurring-list-count">{t('recurring.list.instances', { completed: rule.stats?.completed_instances || 0, total: rule.stats?.total_instances || 0 })}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="recurring-cards">
                    {rules.map(rule => {
                        const subject = getSubjectForRule(rule);
                        const rate = getCompletionRate(rule);
                        return (
                            <div key={rule.rule_id} className="recurring-card" style={{ borderTopColor: subject?.color || '#ccc' }} onClick={() => loadRuleToForm(rule)}>
                                <div className="recurring-card-header">
                                    <div className="recurring-card-title">{rule.title || subject?.subjectname || ''}</div>
                                </div>
                                <div className="recurring-card-meta">
                                    {subject?.subjectname || ''} - {getRecurrenceDescription(rule, t)}
                                </div>
                                <div className="recurring-card-progress">
                                    <div className="recurring-card-progress-bar" style={{ width: `${rate}%`, backgroundColor: subject?.color || '#4caf50' }} />
                                </div>
                                <div className="recurring-card-footer">
                                    <span>{t('recurring.list.completionRate')}: {rate}%</span>
                                    <span>{t('recurring.list.instances', { completed: rule.stats?.completed_instances || 0, total: rule.stats?.total_instances || 0 })}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Preview dialog */}
            {showPreview && previewData && (
                <RecurringPreview
                    dates={previewData.dates || []}
                    scheduleType={form.schedule_type}
                    scheduledTime={Number(form.scheduled_time)}
                    startTimeOfDay={form.start_time_of_day}
                    subjectColor={selectedSubject?.color}
                    title={form.special_text || selectedSubject?.subjectname || ''}
                    onClose={() => setShowPreview(false)}
                />
            )}

            {/* Clock Picker Popup */}
            {showClockPicker && (
                <ClockPicker
                    value={form.start_time_of_day}
                    onChange={(val) => updateField('start_time_of_day', val)}
                    onClose={() => setShowClockPicker(false)}
                />
            )}

            {/* Subject Picker Popup */}
            {showSubjectPicker && (
                <div className="recurring-popup-overlay" onClick={() => { setShowSubjectPicker(false); setSubjectSearch(''); }}>
                    <div className="recurring-popup" onClick={e => e.stopPropagation()}>
                        <div className="recurring-popup-header">
                            <h3>{t('recurring.form.subject')}</h3>
                            <button className="recurring-popup-close" onClick={() => { setShowSubjectPicker(false); setSubjectSearch(''); }}>&times;</button>
                        </div>
                        <input type="text" className="recurring-popup-search" placeholder={t('admin.users.search')} value={subjectSearch} onChange={e => setSubjectSearch(e.target.value)} autoFocus />
                        <div className="recurring-popup-list">
                            {filteredSubjects.map(s => (
                                <div key={s.subject_id} className={`recurring-popup-item ${Number(form.subject_id) === s.subject_id ? 'selected' : ''}`} onClick={() => selectSubject(s)}>
                                    <span className="rc-subject-dot" style={{ backgroundColor: s.color || '#ccc' }} />
                                    <span className="recurring-popup-item-name">{s.subjectname}</span>
                                </div>
                            ))}
                            {filteredSubjects.length === 0 && <div className="recurring-popup-empty">{t('admin.users.noResults')}</div>}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                confirmLabel={t('delete')}
                isDanger={true}
            />
        </div>
    );
};

export default RecurringSchedule;
